import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingMelkbosstrandPage() {
  // SEO Configuration
  useSEO({
    title: 'Melkbosstrand Professional Painters - Coastal Painting Specialists | Residential & Commercial',
    description: 'Expert coastal painting services in Melkbosstrand, Atlantic Beach, and Duynefontein. Specialized in weather-resistant coatings for beachfront properties. Over 14 years of experience with guaranteed quality workmanship.',
    keywords: 'painters Melkbosstrand, coastal painting Melkbosstrand, house painters Melkbosstrand, residential painting, commercial painting, beachfront painting',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Melkbosstrand',
          description: 'Coastal painting specialists in Melkbosstrand',
          address: 'Melkbosstrand, Cape Town',
          priceRange: '$$',
          serviceArea: ['Melkbosstrand', 'Atlantic Beach', 'Duynefontein', 'West Beach', 'Big Bay']
        }),
        generateServiceSchema({
          name: 'Coastal Painting Services',
          description: 'Specialized coastal and residential painting services',
          provider: 'Painters Melkbosstrand',
          areaServed: ['Melkbosstrand', 'Atlantic Beach', 'Duynefontein'],
          serviceType: ['Coastal Painting', 'Residential Services', 'Commercial Projects']
        })
      ]
    }
  });

  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Painters Melkbosstrand data
  const listing = {
    name: "Painters Melkbosstrand",
    location: "Melkbosstrand, Cape Town",
    website: "https://paintersmelkbosstrand.co.za/",
    heroImage: "https://public.readdy.ai/ai/img_res/9781240a7bf1d27eec9a3f1881e76d35.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=freshly%20painted%20coastal%20home%20exterior%20with%20white%20walls%20modern%20architecture%20ocean%20backdrop%20professional%20finish%20bright%20daylight&width=800&height=600&seq=gallery5&orientation=landscape",
      "https://readdy.ai/api/search-image?query=interior%20room%20painting%20professional%20painter%20applying%20paint%20to%20walls%20with%20roller%20clean%20workspace%20coastal%20home%20setting&width=800&height=600&seq=gallery6&orientation=landscape",
      "https://readdy.ai/api/search-image?query=exterior%20house%20painting%20project%20with%20scaffolding%20painters%20working%20on%20coastal%20property%20modern%20seaside%20architecture&width=800&height=600&seq=gallery7&orientation=landscape",
      "https://readdy.ai/api/search-image?query=beautifully%20painted%20coastal%20living%20room%20with%20ocean%20views%20elegant%20color%20scheme%20modern%20furniture%20natural%20lighting%20professional%20finish&width=800&height=600&seq=gallery8&orientation=landscape"
    ],
    services: [
      {
        title: "Coastal Painting",
        description: "Specialized painting services for coastal properties using marine-grade paints that withstand salt air, wind, and harsh weather conditions.",
        features: ["Salt-resistant coatings", "Wind-resistant finishes", "UV protection", "Moisture barriers"]
      },
      {
        title: "Residential Services",
        description: "Complete residential painting solutions for homes in Melkbosstrand and surrounding coastal areas, from single rooms to entire properties.",
        features: ["Interior painting", "Exterior painting", "Roof coating", "Waterproofing"]
      },
      {
        title: "Commercial Projects",
        description: "Professional painting services for commercial properties, offices, and retail spaces with minimal disruption to your business operations.",
        features: ["Office buildings", "Retail spaces", "Restaurants", "Hotels"]
      }
    ],
    serviceAreas: ["Melkbosstrand", "Atlantic Beach", "Duynefontein", "Silwerstroomstrand", "Sunset Beach", "Big Bay"],
    about: "Painters Melkbosstrand is a premier painting company specializing in coastal property painting services in Melkbosstrand and the surrounding Atlantic Seaboard areas. With extensive experience in dealing with the unique challenges of coastal environments, we have become the go-to painting contractor for homeowners and businesses in the area.",
    aboutExtended: [
      "Our team understands the specific demands that coastal weather places on painted surfaces. Salt spray, strong winds, intense UV radiation, and high humidity levels require specialized painting solutions. We use only premium marine-grade paints and coatings specifically formulated to withstand these harsh coastal conditions, ensuring your property maintains its beautiful appearance for years to come.",
      "We specialize in both residential and commercial painting projects, offering comprehensive services that include surface preparation, repairs, priming, painting, and protective coating applications. Our expertise covers interior painting, exterior painting, roof coating, waterproofing, and specialized decorative finishes tailored to coastal properties.",
      "What distinguishes Painters Melkbosstrand is our commitment to quality workmanship and customer satisfaction. Every project begins with a thorough assessment of your property's specific needs, taking into account exposure to elements, existing surface conditions, and your aesthetic preferences. We provide honest recommendations and transparent pricing, ensuring you receive the best value for your investment."
    ],
    ourApproach: [
      "Comprehensive property assessment and consultation",
      "Specialized surface preparation for coastal conditions",
      "Premium marine-grade paints and protective coatings",
      "Professional application by experienced coastal painters",
      "Weather-appropriate scheduling for optimal results",
      "Complete cleanup and quality inspection",
      "Extended warranty on coastal painting projects"
    ],
    whyChoose: [
      "Coastal painting specialists",
      "Marine-grade materials",
      "Weather-resistant solutions",
      "Experienced local team",
      "Competitive pricing",
      "Quality guaranteed",
      "Free color consultation",
      "Fully insured"
    ],
    testimonials: [
      {
        name: "David Thompson",
        rating: 5,
        text: "Excellent service from start to finish. They really understand coastal painting and used the right products for our beachfront property. The paint still looks perfect after two years!"
      },
      {
        name: "Amanda Botha",
        rating: 5,
        text: "Professional team who completed our home painting on time and within budget. They were respectful of our property and the quality is outstanding."
      },
      {
        name: "James Wilson",
        rating: 5,
        text: "Highly recommend Painters Melkbosstrand. They transformed our weathered coastal home into a stunning property. Great attention to detail and excellent customer service."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Painters Melkbosstrand</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Painters Melkbosstrand</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We proudly serve the following coastal areas in Cape Town's Atlantic Seaboard:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">We offer specialized coastal painting services designed to protect and beautify your property in the challenging marine environment.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Melkbosstrand, Atlantic Beach, Duynefontein</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 8AM-5PM</p>
                    <p className="text-gray-600">Sat: 9AM-1PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Table View</li>
                <li>Melkbosstrand</li>
                <li>Durbanville</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
